import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import { supabase } from "../supabaseClient";
import { logLogin } from "../utils/activityLogger";
import NebulaIntro from "./NebulaIntro";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [introTarget, setIntroTarget] = useState("/dashboard");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ── Admin bypass — before any Supabase auth call ──
    if (
      email === process.env.REACT_APP_ADMIN_EMAIL &&
      password === process.env.REACT_APP_ADMIN_PASSWORD
    ) {
      sessionStorage.setItem("nebula_admin", "true");
      // Show intro then go to admin
      setIntroTarget("/admin");
      setShowIntro(true);
      return;
    }

    // ── Regular user login ────────────────────────────
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      logLogin();

   const { data: compUser } = await supabase
  .from("company_users")
  .select("company_id")
  .eq("user_id", data.user.id)
  .maybeSingle();

let target;

if (compUser?.company_id) {
  // User belongs to a company — check approval status
  const { data: reg } = await supabase
    .from("user_registrations")
    .select("status")
    .eq("user_id", data.user.id)
    .maybeSingle();

  target = reg?.status === "approved" ? "/dashboard" : "/pending-approval";
} else {
  // Brand new user — check if onboarding done
  const { data: profile } = await supabase
    .from("company_profiles")
    .select("onboarding_complete")
    .eq("user_id", data.user.id)
    .single();

  target = !profile || !profile.onboarding_complete ? "/onboarding" : "/dashboard";
}

setIntroTarget(target);
setShowIntro(true); // 🎬 play the intro!
    } catch (error) {
      toast.error(error.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // When intro animation finishes, navigate to destination
  const handleIntroComplete = () => {
    toast.success("Welcome back!");
    navigate(introTarget);
  };

  return (
    <>
      {/* 🎬 Full-screen intro overlay — plays after login success */}
      {showIntro && <NebulaIntro onComplete={handleIntroComplete} />}

      <div className="min-h-screen bg-[#0a0b0f] flex">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>

            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center">
                <span className="text-white font-bold text-xl">N</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Welcome Back</h1>
                <p className="text-gray-400 text-sm">
                  Sign in to your Nebula Finance AI account
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="nebula-input"
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="nebula-input pr-12"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  <div style={{ textAlign: "right", marginTop: -8 }}>
                    <Link
                      to="/forgot-password"
                      style={{ fontSize: 12, color: "#818cf8", textDecoration: "none" }}
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="nebula-btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            <p className="mt-8 text-center text-gray-400">
              Don't have an account?{" "}
              <Link to="/signup" className="text-indigo-400 hover:text-indigo-300 font-medium">
                Create Account
              </Link>
            </p>
          </div>
        </div>

        <div className="hidden lg:flex flex-1 items-center justify-center bg-gradient-to-br from-indigo-900/20 to-purple-900/20 border-l border-[#2a2d3a]">
          <div className="text-center px-12">
            <div className="w-32 h-32 mx-auto mb-8 rounded-3xl gradient-primary flex items-center justify-center">
              <span className="text-white text-6xl font-bold">N</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Nebula Finance AI</h2>
            <p className="text-gray-400 max-w-sm">
              Access real-time FX rates, treasury insights, and comprehensive financial tools.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
